/**
 * Comic generation event handlers.
 */

import type { Socket } from "socket.io-client";
import type {
  ServerToClientEvents,
  ClientToServerEvents,
} from "@amzn/a4k-creator-studio-shared";
import { useComicStore } from "../comicStore";
import { useStoryStore } from "../storyStore";
import { useErrorStore } from "../errorStore";
import { TOAST_DURATION } from "../../components/toastDurations";
import { useSocketStore, isVoiceLocked } from "../socketStore";
import { ingestImage } from "../../lib/imageLoader";
import minervaService from "../../services/minervaService";
import { AppEvent } from "../../services/appEvents";
import { trackComic, trackErrorToast, Comic } from "../../services/metrics";
import { generateRequestedAt } from "../../services/comicGenerator";

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

/** Comic events that mutate the on-screen comic and so must be ignored when they
 *  belong to a different comic (see onComicEvent). */
type ComicMutationEvent =
  | "comicPagesAdded"
  | "comicPageGenerating"
  | "comicLayoutComplete"
  | "comicPageGenerated"
  | "comicPageError"
  | "comicGenComplete"
  | "comicInvalidated"
  | "comicNarrationReady";

type ComicEventData<E extends ComicMutationEvent> = Parameters<ServerToClientEvents[E]>[0];

export function registerComicHandlers(socket: TypedSocket): void {
  // A generation runs detached on the server and keeps emitting to this socket
  // even after the user navigates to another project. Registering comic-mutating
  // events through this wrapper drops events for a different comic at one choke
  // point, so the guard is structural — a new comic event can't reintroduce the
  // cross-project bleed by forgetting an inline check.
  const onComicEvent = <E extends ComicMutationEvent>(
    event: E,
    handler: (data: ComicEventData<E>) => void,
  ): void => {
    const guarded = (data: ComicEventData<E>): void => {
      if (useComicStore.getState().comicId !== data.comicId) return;
      handler(data);
    };
    (socket.on as (e: E, l: (d: ComicEventData<E>) => void) => void)(event, guarded);
  };

  socket.on("comicError", (data) => {
    // A comicId-tagged error from a detached generation for another project must
    // not reset the comic on screen. Errors without a comicId are pre-creation
    // failures for the user's current action — always surfaced.
    if (data.comicId && useComicStore.getState().comicId !== data.comicId) return;
    const isRecoverable = !!(data.recoverable && data.hint);
    const message = isRecoverable
      ? data.hint!
      : "Something went wrong making your book. Do you have a different story idea?";
    // Prefer the server's structured category (typed error classification) so
    // client engagement metrics agree with the server's ComicError dashboard;
    // fall back to the legacy message-prefix parse for older servers.
    const errorCategory = data.category || data.error?.split(":")[0] || "unknown";
    trackErrorToast("Comic", `generation_failed.${errorCategory}`);
    useErrorStore.getState().addError(message, "info", isRecoverable ? TOAST_DURATION.SHORT : TOAST_DURATION.LONG);
    useComicStore.getState().setGenState("idle");
    minervaService.recordAppEvent(AppEvent.COMIC_ERROR);
    trackComic(Comic.GenerateError, errorCategory);

    // For recoverable blocks, restart voice so the kid can keep editing their story
    if (isRecoverable) {
      useSocketStore.getState().startVoice();
    }
  });

  let firstPageTracked = false;
  socket.on("comicPromptsReady", (data) => {
    firstPageTracked = false;
    // comicPromptsReady establishes the current comicId, so it can't gate on it
    // like the events below. Instead gate on storyId: a generation started for
    // project A can finish its layout AFTER the user opened project B (Claude
    // layout takes seconds), and without this its comicPromptsReady would adopt
    // A's comicId into B's view. Only adopt when this comic's project is the one
    // currently loaded (a positive match — a blank/unloaded story never adopts).
    if (useStoryStore.getState().storyId !== data.storyId) {
      return;
    }
    if (data.rawResponse) {
      useComicStore.getState().setRawClaudeResponse(data.rawResponse);
    }
    useComicStore.setState({ comicId: data.comicId });
    const pages = (data.pages || []).map((p) => ({
      ...p,
      id: `page-${p.pageNumber}`,
    }));
    useComicStore.getState().addPages(pages);
    useComicStore.getState().setGenState("generating_pages");
  });

  onComicEvent("comicPagesAdded", (data) => {
    const pages = (data.pages || []).map((p) => ({
      ...p,
      id: `page-${p.pageNumber}`,
    }));
    useComicStore.getState().addPages(pages);
  });

  onComicEvent("comicPageGenerating", (data) => {
    useComicStore.getState().setCurrentDrawingPage(data.pageNumber);
  });

  onComicEvent("comicLayoutComplete", (data) => {
    useComicStore.getState().setLayoutComplete(data.totalPages);
  });

  onComicEvent("comicPageGenerated", (data) => {
    if (!firstPageTracked && generateRequestedAt > 0) {
      trackComic(Comic.TimeToFirstPage, undefined, Date.now() - generateRequestedAt);
      firstPageTracked = true;
    }
    if (data.debug) {
      useComicStore.getState().setPageDebug(data.pageNumber, data.debug.prompt, data.debug.referenceImages);
    }
    if (data.imageId) {
      useComicStore.getState().setPageImageId(data.pageNumber, data.imageId);
      if (data.imageBase64) ingestImage(data.imageId, data.imageBase64);
    } else if (data.imageBase64) {
      useComicStore.getState().setPageImage(data.pageNumber, data.imageBase64);
    }
  });

  onComicEvent("comicPageError", (data) => {
    useComicStore.getState().setPageImageError(data.pageNumber);
    trackComic(Comic.GeneratePageError, String(data.pageNumber));
  });

  onComicEvent("comicGenComplete", () => {
    useComicStore.getState().setGenState("done");
    trackComic(Comic.GenerateComplete, undefined, useComicStore.getState().pages.length);
  });

  // Server reset a done book to idle because a generation input changed —
  // return the "Generate book" button. Guarded to the current comic by onComicEvent.
  onComicEvent("comicInvalidated", () => {
    if (useComicStore.getState().genState === "done") {
      useComicStore.getState().setGenState("idle");
    }
  });

  onComicEvent("comicNarrationReady", (data) => {
    const store = useComicStore.getState();
    for (const { pageNumber, audioBase64, speechMarks } of data.pages) {
      store.updatePageAudio(pageNumber, audioBase64, speechMarks);
    }
  });

  socket.on("comicSaved", (data) => {
    useComicStore.getState().setSaveResult(data.comicId, { saved: true });
  });

  socket.on("comicSaveFailed", (data) => {
    minervaService.recordAppEvent(AppEvent.ESS_SAVE_FAILURE);
    useComicStore.getState().setSaveResult(data.comicId, { saved: false, error: data.error });
  });

  socket.on("voiceModeChanged", () => {
    // voiceModeChanged fires when generation completes and the server resets
    // voiceMode to "creating". At the same time, streamComplete fires and
    // triggers startVoice(). To avoid a double-restart storm, skip if voice
    // was already started recently (startVoice holds voiceLock for 300ms).
    if (isVoiceLocked()) return;
    const { isVoiceActive } = useSocketStore.getState();
    if (isVoiceActive) {
      useSocketStore.getState().restartVoice().catch((err) =>
        console.warn("Failed to restart voice after mode change:", err)
      );
    }
  });
}
